import type { ModalStaticFunctions } from 'antd/es/modal/confirm';
import type { ReactNode } from 'react';

import { UnsavedChangesScopeEnum } from '../enums/UnsavedChangesScopeEnum';

export interface GuardedFormModalRegistration {
  isDirty: boolean;
  onDiscard?: () => void;
}

export interface OpenGuardedFormModalOptions {
  modal: Omit<ModalStaticFunctions, 'warn'>;
  title: ReactNode;
  content: ReactNode;
  getRegistration?: () => GuardedFormModalRegistration | null;
  modalProps?: Parameters<ModalStaticFunctions['confirm']>[0];
  requestLeave: () => Promise<boolean>;
  scope?: UnsavedChangesScopeEnum;
}

export interface GuardedFormModalInstance {
  destroy: () => void;
  update: (config: Parameters<ModalStaticFunctions['confirm']>[0]) => void;
}

/**
 * Opens an antd confirm modal and routes close attempts through the global unsaved-changes guard.
 * Forms should register dirty state via `useFormDirtyState` / `useRegisterUnsavedChanges` (scope modal).
 */
export function openGuardedFormModal({
  modal,
  title,
  content,
  modalProps,
  requestLeave,
}: OpenGuardedFormModalOptions): GuardedFormModalInstance {
  let modalInstance: GuardedFormModalInstance | null = null;

  const destroyModal = () => {
    modalInstance?.destroy();
  };

  const closeWithGuard = async (): Promise<void> => {
    const canLeave = await requestLeave();
    if (canLeave) {
      destroyModal();
    }
  };

  modalInstance = modal.confirm({
    centered: true,
    closable: true,
    destroyOnHidden: true,
    footer: null,
    icon: null,
    mask: {
      closable: false,
    },
    onCancel: () => {
      void closeWithGuard();
    },
    title,
    width: 600,
    ...modalProps,
    content,
  }) as GuardedFormModalInstance;

  return {
    destroy: destroyModal,
    update: (config) => modalInstance?.update(config),
  };
}

export interface CreateGuardedModalCancelHandlerOptions {
  requestLeave: () => Promise<boolean>;
  onConfirmed: () => void;
  resetForm?: () => void;
}

/** Use as Form Cancel handler inside guarded modals. */
export function createGuardedModalCancelHandler({
  requestLeave,
  onConfirmed,
  resetForm,
}: CreateGuardedModalCancelHandlerOptions): () => void {
  return () => {
    void (async () => {
      const canLeave = await requestLeave();
      if (!canLeave) {
        return;
      }

      resetForm?.();
      onConfirmed();
    })();
  };
}
